import { reactive, ref, computed, type Ref, type ShallowRef } from "vue";
import { useRoute } from "vue-router";
import { ApolloQueryResult } from "@apollo/client/core";
import { getWordList } from "@/common/graphql/expression";
import { getChainInfo } from "@/common/graphql/chain";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetPrefByChainQuery,
  GetPrefByChainDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
} from "@/generated/graphql";
import searchConditionStore from "@/store/searchConditionStore";
import type { AreaListItem } from "../types";
import { SCHEDULED_DELIVERY_LIST_FILE_NAME } from "../../admin/ScheduledDeliveryList/constants";

interface UseRegularDeliveryInitParams {
  overlay: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  areaListItems: Ref<AreaListItem[] | null | undefined>;
  shopSelectFromValues: Ref<{ shop_code: string; name: string }>;
  shopSelectToValues: Ref<{ shop_code: string; name: string }>;
}

interface RunInitialLoadCallbacks {
  onClickListTab: () => Promise<void>;
  onClickSearch: () => void;
  showDeliveryAvailability: (area_code: string | null) => Promise<void>;
}

/**
 * 画面表示時に必要な初期化処理を提供する composable。
 *
 * @returns 初期化処理を実行する関数群と初期表示に必要な状態
 */
export const useRegularDeliveryInit = ({
  overlay,
  errdialog,
  errMessage,
  areaListItems,
  shopSelectFromValues,
  shopSelectToValues,
}: UseRegularDeliveryInitParams) => {
  const route = useRoute();

  const wordList = reactive<Record<string, string>>({});

  const requiredFieldLabelKeys = [
    "lbl_zipcode",
    "lbl_tel4",
    "lbl_member_id",
    "lbl_name_kanji_kana",
    "lbl_delivery_area_name",
  ] as const;

  const isWordListReady = computed(() => {
    if (Object.keys(wordList).length === 0) return false;
    return requiredFieldLabelKeys.every((key) => key in wordList);
  });

  //ハウスカード表示
  const house_card_display = ref<number>(0);

  // 他店舗会員非表示フラグ
  const is_hide_other_shop_members = ref<boolean | null | undefined>(null);

  /**
   * チェーンリスト
   */
  const chainListItems = ref<
    (
      | { __typename?: "Chain" | undefined; chain_code: string; name: string }
      | null
      | undefined
    )[]
    | null
    | undefined
  >(null);
  const chainSelectValues = ref<{ chain_code: string; name: string } | null | undefined>(
    {
      chain_code: "",
      name: "",
    }
  );

  /**
   * 出荷元リスト
   */
  const shopListFromItems = ref<
    (
      | { __typename?: "Shop" | undefined; shop_code: string; name: string }
      | null
      | undefined
    )[]
    | null
    | undefined
  >([]);

  /**
   * 配達先リスト
   */
  const shopListToItems = ref<
    (
      | { __typename?: "Shop" | undefined; shop_code: string; name: string }
      | null
      | undefined
    )[]
    | null
    | undefined
  >([]);

  const prefListItems = ref<(string | null | undefined)[] | null | undefined>(
    []
  );

  const items = ref<unknown[]>([]);

  //タブ
  const tab = ref<string>("tab1");

  /**
   * 画面初期ロード
   */
  const runInitialLoad = async (callbacks: RunInitialLoadCallbacks): Promise<void> => {
    const { onClickListTab, onClickSearch, showDeliveryAvailability } = callbacks;
    try {
      overlay.value = true;

      //文言データを取得する
      Object.assign(
        wordList,
        await getWordList(
          localStorageGetChainCode(),
          "RegularDelivery",
          localStorageGetCustomerCode()
        )
      );

      //graphql接続
      const apolloClient = await generateApolloClient();

      //チェーン情報を取得する
      const result = await getChainInfo(
        localStorageGetCustomerCode(),
        localStorageGetChainCode()
      );
      if (result !== undefined && result !== null) {
        house_card_display.value = result.house_card_display
          ? result.house_card_display
          : 0;
        // 他店舗会員非表示フラグを取得
        is_hide_other_shop_members.value = result.is_hide_other_shop_members;
      }

      /**
       * 都道府県データ
       */
      const prefList: ApolloQueryResult<GetPrefByChainQuery> =
        await apolloClient.query<GetPrefByChainQuery>({
          query: GetPrefByChainDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
          },
          fetchPolicy: "no-cache",
        });
      prefListItems.value = prefList.data.getPrefByChain as unknown as (
        | string
        | null
        | undefined
      )[];

      /**
       * エリアデータ取得処理
       */
      const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
        await apolloClient.query<
          ListDeliveryAreaQuery,
          ListDeliveryAreaQueryVariables
        >({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
            shop_code: localStorageGetShopCode(),
          },
        });
      areaListItems.value = listArea.data.listDeliveryArea;

      //this.getDeliveryAvailable();
      //interval = setInterval(async () => {
      //  this.getDeliveryAvailable();
      //}, 30000);

      //編集画面からの戻り
      if (
        (route.params !== null &&
          route.params !== undefined &&
          // @ts-expect-error string | undefined 許容
          route.params.searchMode) ||
        (route.query !== null &&
          route.query !== undefined &&
          // @ts-expect-error string | undefined 許容
          route.query.searchMode)
      ) {
        await onClickListTab();

        //検索条件を取得する
        const searchCondition: Map<string, string> =
          await searchConditionStore.getCondition(
            SCHEDULED_DELIVERY_LIST_FILE_NAME
          );

        //検索条件を削除する
        await searchConditionStore.clearCondition(
          SCHEDULED_DELIVERY_LIST_FILE_NAME
        );

        if (searchCondition !== null && searchCondition !== undefined) {
          //チェーン情報を設定する
          chainSelectValues.value = {
            chain_code: searchCondition.get("chain_code")
              ? searchCondition.get("chain_code")!
              : "",
            name: searchCondition.get("chain_name")
              ? searchCondition.get("chain_name")!
              : "",
          };

          //出荷元店舗情報を設定する
          shopSelectFromValues.value = {
            shop_code: searchCondition.get("shop_code_from")
              ? searchCondition.get("shop_code_from")!
              : "",
            name: searchCondition.get("shop_code_from_name")
              ? searchCondition.get("shop_code_from_name")!
              : "",
          };

          //配達先店舗情報を設定する
          shopSelectToValues.value = {
            shop_code: searchCondition.get("shop_code_to")
              ? searchCondition.get("shop_code_to")!
              : "",
            name: searchCondition.get("shop_code_to_name")
              ? searchCondition.get("shop_code_to_name")!
              : "",
          };

          //検索
          onClickSearch();
        }
      } else {
        /**
         * 都道府県データ
         */
        const prefList2: ApolloQueryResult<GetPrefByChainQuery> =
          await apolloClient.query<GetPrefByChainQuery>({
            query: GetPrefByChainDocument,
            variables: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
            },
            fetchPolicy: "no-cache",
          });
        prefListItems.value = prefList2.data.getPrefByChain as unknown as (
          | string
          | null
          | undefined
        )[];

        // 便選択の初期化
        await showDeliveryAvailability(null);
        /**
         * エリアデータ取得処理
         */
        const listArea2: ApolloQueryResult<ListDeliveryAreaQuery> =
          await apolloClient.query<
            ListDeliveryAreaQuery,
            ListDeliveryAreaQueryVariables
          >({
            query: ListDeliveryAreaDocument,
            variables: {
              chain_code: localStorageGetChainCode(),
              customer_code: localStorageGetCustomerCode(),
              shop_code: localStorageGetShopCode(),
            },
          });
        areaListItems.value = listArea2.data.listDeliveryArea;
      }
    } catch (err: unknown) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    } finally {
      overlay.value = false;
    }
  };

  return {
    wordList,
    isWordListReady,
    house_card_display,
    is_hide_other_shop_members,
    chainListItems,
    chainSelectValues,
    shopListFromItems,
    shopListToItems,
    prefListItems,
    items,
    tab,
    runInitialLoad,
  };
};
